// Pulling addresses out of a pasted block.
//
// The block arrives from wherever the person got it — a mail client's To line,
// a spreadsheet column, a chat message — so it may be separated by commas,
// semicolons, newlines, angle brackets, or by nothing at all. That last case is
// what the naive "split on punctuation" approach gets wrong: copying a column
// out of some spreadsheets yields `ann@acme.comBob@acme.com`, and there is no
// separator left to split on.
//
// What still marks the boundary is the top-level domain. So the scan walks the
// text address by address: find an `@`, read backwards for the local part, then
// read forwards to where the domain ends at a TLD — and hand whatever follows
// back to the scan as the beginning of the next address.

// TLDs common enough to be worth recognizing by name. This list is not the
// authority on which addresses are valid — an unlisted TLD still parses when
// the addresses are separated normally (see the fallback in splitDomain). What
// the list buys is the ability to find the seam in a run-together paste.
const TLDS = new Set([
  // generic
  'com', 'net', 'org', 'edu', 'gov', 'mil', 'int', 'info', 'biz', 'name', 'pro',
  'app', 'dev', 'io', 'ai', 'co', 'me', 'tv', 'cc', 'xyz', 'online', 'site',
  'tech', 'store', 'cloud', 'email', 'group', 'inc', 'llc', 'ltd', 'agency',
  'solutions', 'services', 'systems', 'works', 'construction', 'contractors',
  'engineering', 'energy', 'industries', 'supply', 'equipment', 'management',
  // country codes seen on North American and European business mail
  'us', 'ca', 'uk', 'ie', 'de', 'fr', 'es', 'it', 'nl', 'be', 'ch', 'at', 'se',
  'no', 'dk', 'fi', 'pl', 'pt', 'gr', 'cz', 'ro', 'hu', 'ru', 'ua', 'tr', 'il',
  'ae', 'sa', 'za', 'ng', 'ke', 'eg', 'au', 'nz', 'jp', 'cn', 'hk', 'sg', 'in',
  'kr', 'my', 'ph', 'th', 'vn', 'id', 'mx', 'br', 'ar', 'cl', 'pe', 'eu',
]);

// Endings that only make sense as a pair: seeing `co` in `acme.co.uk` must not
// end the domain there. Checked before a single-label TLD is accepted.
const COMPOUND_TLDS = new Set([
  'co.uk', 'org.uk', 'ac.uk', 'gov.uk', 'me.uk', 'ltd.uk', 'plc.uk',
  'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au',
  'co.nz', 'org.nz', 'net.nz', 'govt.nz',
  'co.za', 'org.za', 'co.jp', 'ne.jp', 'or.jp', 'co.kr', 'co.in', 'co.il',
  'com.br', 'com.mx', 'com.ar', 'com.sg', 'com.hk', 'com.my', 'com.tr',
  'com.cn', 'net.cn', 'org.cn', 'com.ph', 'com.vn', 'com.pe', 'com.co',
]);

// Everything outside this set is treated as a separator. Keeping it this narrow
// is what turns `"Ann Lee" <ann@acme.com>;` into something the scan can read.
const NOT_ADDRESS_CHAR = /[^A-Za-z0-9.@_%+-]/g;
const LOCAL_CHAR = /[A-Za-z0-9._%+-]/;
const DOMAIN_CHAR = /[A-Za-z0-9.-]/;

// Mirrors looks_like_email() in the API: shaped like an address, no judgement
// about whether it exists.
const EMAIL_RE = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;

export const isValidEmail = (value) =>
  typeof value === 'string' && EMAIL_RE.test(value.trim());

// The longest known TLD that starts `label`, or '' if none does. Longest wins
// so `comjane` yields `com` rather than `co`.
const leadingTld = (label) => {
  let best = '';
  for (let len = 1; len <= label.length; len += 1) {
    const candidate = label.slice(0, len);
    if (TLDS.has(candidate) && candidate.length > best.length) best = candidate;
  }
  return best;
};

// A capital followed by a lowercase letter, immediately after a TLD, is where
// the domain stopped and something else started: `acme.netEli Stone`. Nobody
// writes a domain that way, and requiring the lowercase after the capital keeps
// a shouted `ACME.INTERNAL` from looking like a seam at `INT`.
const startsNewWord = (rest) => /^[A-Z][a-z]/.test(rest);

// Where the domain ends inside `run`, the stretch of domain-legal characters
// following an '@'. Returns the domain, or '' when the run holds nothing
// domain-shaped.
//
// Splitting a run is only right when a separator was lost. Guessing wrong in
// either direction costs an address, so the split has to be evidenced rather
// than assumed: `acme.internal` opens with the real TLD `int` and must survive
// whole, while `acme.netEli` has to be cut at `net`. Two signals say a
// separator went missing — `jammed`, meaning the run is followed straight away
// by another '@' and so ends in the next address's local part, and a
// capitalized word starting where the TLD ends.
const splitDomain = (run, jammed) => {
  const labels = run.split('.');
  const wholeRun =
    labels.length >= 2 && /^[A-Za-z]{2,}$/.test(labels[labels.length - 1]) ? run : '';

  // Everything after the domain has to be evidence of a seam; otherwise the
  // run is one domain and the shorter reading was a false TLD sighting.
  const accept = (domain) => {
    const rest = run.slice(domain.length);
    if (!rest) return domain;
    return jammed || startsNewWord(rest) ? domain : wholeRun;
  };

  // A label that is exactly a TLD ends the domain. Checked before any prefix
  // matching, which is what keeps `acme.co.uk` from stopping at `ac` in `acme`.
  for (let k = 1; k < labels.length; k += 1) {
    if (!TLDS.has(labels[k])) continue;
    const next = k + 1 < labels.length ? leadingTld(labels[k + 1]) : '';
    if (next && COMPOUND_TLDS.has(`${labels[k]}.${next}`)) {
      return accept(`${labels.slice(0, k + 1).join('.')}.${next}`);
    }
    return accept(labels.slice(0, k + 1).join('.'));
  }

  // Otherwise the seam falls inside a label: `acme.comBob` ends at `com`.
  for (let k = 1; k < labels.length; k += 1) {
    const tld = leadingTld(labels[k]);
    if (tld) return accept(`${labels.slice(0, k).join('.')}.${tld}`);
  }

  // Two addresses ran together on a TLD nobody recognizes. There's no seam to
  // find, so the pair comes through as one visibly wrong entry to be deleted,
  // rather than being dropped without a trace.
  return wholeRun;
};

/**
 * Extract addresses from a pasted block, lowercased and de-duplicated, in the
 * order they appear.
 *
 * The result is meant to be shown back for checking rather than trusted: a
 * paste that ran two names together produces something odd-looking here, and
 * odd-looking is the point — it can be spotted and removed before any accounts
 * are created.
 */
export const parseEmails = (text) => {
  if (typeof text !== 'string' || !text) return [];
  const cleaned = text.replace(NOT_ADDRESS_CHAR, ' ');

  const found = [];
  const seen = new Set();
  let cursor = 0;

  while (cursor < cleaned.length) {
    const at = cleaned.indexOf('@', cursor);
    if (at < 0) break;

    // Local part: backwards from the '@', stopping at the previous address's
    // last character so one address can't borrow the tail of another.
    let start = at;
    while (start > cursor && LOCAL_CHAR.test(cleaned[start - 1])) start -= 1;
    while (start < at && /[.+-]/.test(cleaned[start])) start += 1; // can't lead a local part
    const local = cleaned.slice(start, at);

    // Domain: forwards to the end of the domain-legal run, then trimmed to
    // where the TLD says the address ends.
    let end = at + 1;
    while (end < cleaned.length && DOMAIN_CHAR.test(cleaned[end])) end += 1;
    const run = cleaned.slice(at + 1, end).replace(/[.-]+$/, ''); // trailing punctuation
    const domain = splitDomain(run, cleaned[end] === '@');

    if (!local || !domain) {
      cursor = at + 1;
      continue;
    }

    const email = `${local}@${domain}`.toLowerCase();
    if (EMAIL_RE.test(email) && !seen.has(email)) {
      seen.add(email);
      found.push(email);
    }
    cursor = at + 1 + domain.length;
  }

  return found;
};
